import { useState } from 'react';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import { doc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

// ✅ FIXED: Changed from via.placeholder.com to Unsplash
const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1560184897-ae75f418493e?auto=format&fit=crop&w=100&q=80';

export default function PropertyTable({ properties, onDelete }) {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function handleDelete(id) {
    if (window.confirm('Are you sure you want to delete this property?')) {
      setLoading(true);
      try {
        await deleteDoc(doc(db, 'listings', id));
        toast.success('Property deleted successfully');
        onDelete(id);
      } catch (error) {
        console.error('Error deleting property:', error);
        toast.error('Failed to delete property');
      } finally {
        setLoading(false);
      }
    }
  }

  if (!properties || properties.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg shadow">
        <p className="text-gray-500">No properties found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Property</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Owner</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {properties.map((property) => {
              // 🔥 CRITICAL FIX: SAFE IMAGE GUARD
              const hasImages = property && 
                Array.isArray(property.imgUrls) && 
                property.imgUrls.length > 0;
              const imageUrl = hasImages ? property.imgUrls[0] : FALLBACK_IMAGE;

              return (
                <tr key={property.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <img 
                        src={imageUrl}
                        alt={property.name}
                        className="w-12 h-12 rounded object-cover"
                        onError={(e) => { e.target.src = FALLBACK_IMAGE; }}
                      />
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{property.name}</div>
                        <div className="text-sm text-gray-500">
                          {property.bedrooms} bed {property.bathrooms} bath
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      property.type === 'rent' 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-green-100 text-green-800'
                    }`}>
                      {property.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    ₹{property.offer 
                      ? property.discountedPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                      : property.regularPrice.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '')
                    }
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {property.address}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {property.userEmail || 'N/A'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                      Active
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button 
                        onClick={() => navigate(`/category/${property.type}/${property.id}`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View"
                      >
                        <FaEye />
                      </button>
                      <button 
                        onClick={() => navigate(`/edit-listing/${property.id}`)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        onClick={() => handleDelete(property.id)} 
                        disabled={loading}
                        className="text-red-600 hover:text-red-900 disabled:opacity-50"
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
